/**
 * 🥫 PantryScannerPanel — batch multi-item scanner.
 *
 * Owner directive (Feb 2026):
 *   The old EquipmentScannerPanel identified ONE thing at a time,
 *   which the owner called "very limiting". This one snaps the whole
 *   fridge / cupboard / gym corner, runs GPT-5.4 vision on the batch,
 *   snaps every detected item to a curated master catalog, and hands
 *   the user a REVIEW list so they can add / edit / delete before the
 *   items ever hit their pantry.
 *
 * User can also add items purely by typing (catalog autocomplete) or
 * without a photo at all — the catalog + manual add are always live.
 *
 * Props:
 *   space: "kitchen" | "gym"  (kitchen = ingredients, gym = gear)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Camera, Check, X, Trash2, Sparkles, Loader2, Plus, Search, Pencil,
} from "lucide-react";
import { CameraCapture } from "./CameraCapture";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PantryScannerPanel({ space = "kitchen" }) {
  const spaceLabel = space === "kitchen" ? "pantry" : "gear";
  const noun = space === "kitchen" ? "ingredient" : "piece";

  const [saved, setSaved] = useState([]);        // items already in DB
  const [pending, setPending] = useState([]);    // items awaiting user confirm
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [query, setQuery] = useState("");        // autocomplete text
  const [suggestions, setSuggestions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const searchTimer = useRef(null);

  // ── Load user's existing pantry / gear ──
  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pantry/list`, { params: { space } });
      setSaved(r.data?.items || []);
    } catch { /* silent */ }
  }, [space]);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Catalog autocomplete ──
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/pantry/catalog`, {
          params: { space, q: query, limit: 12 },
        });
        setSuggestions(r.data?.items || []);
      } catch { setSuggestions([]); }
    }, 180);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [query, space]);

  // ── Batch scan (multi-item vision) — camera modal → dataUrl → API ──
  const onScan = () => setCameraOpen(true);

  const handleCapture = async (dataUrl) => {
    setCameraOpen(false);
    if (!dataUrl) return;
    setScanning(true);
    try {
      const r = await axios.post(`${API}/pantry/scan`, { photo_b64: dataUrl, space });
      const items = r.data?.items || [];
      if (items.length === 0) {
        toast.info("Didn't spot any items with confidence. Try a closer / clearer shot, or add manually.");
      } else {
        const withIds = items.map((it, i) => ({
          _tmp: `t_${Date.now()}_${i}`,
          name: it.name,
          category: it.category,
          qty: it.qty || "",
          tags: it.tags || [],
          confidence: it.confidence,
          catalog_id: it.catalog_id,
          source: it.source,
        }));
        setPending((prev) => [...withIds, ...prev]);
        toast.success(`Spotted ${items.length} ${noun}${items.length !== 1 ? "s" : ""} — review below.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed.");
    } finally {
      setScanning(false);
    }
  };

  // ── Add manually from catalog / typed ──
  const addFromCatalog = (item) => {
    setPending((prev) => [
      {
        _tmp: `t_${Date.now()}`,
        name: item.name,
        category: item.category,
        qty: "",
        tags: [],
        catalog_id: item.id,
        source: "catalog",
      },
      ...prev,
    ]);
    setQuery("");
    setSuggestions([]);
  };

  const addTypedFallback = () => {
    const n = query.trim();
    if (!n) return;
    setPending((prev) => [
      {
        _tmp: `t_${Date.now()}`,
        name: n,
        category: "misc",
        qty: "",
        tags: [],
        source: "manual",
      },
      ...prev,
    ]);
    setQuery("");
    setSuggestions([]);
  };

  // ── Pending item edits ──
  const patchPending = (tmp, patch) => {
    setPending((prev) => prev.map((it) => (it._tmp === tmp ? { ...it, ...patch } : it)));
  };
  const removePending = (tmp) => {
    setPending((prev) => prev.filter((it) => it._tmp !== tmp));
  };

  // ── Confirm all: save the batch ──
  const onConfirmAll = async () => {
    if (pending.length === 0) return;
    setBusy(true);
    try {
      await axios.post(`${API}/pantry/save`, {
        space,
        items: pending.map((it) => ({
          name: it.name,
          category: it.category,
          qty: it.qty,
          tags: it.tags,
          catalog_id: it.catalog_id,
          source: it.source,
          ai_confidence: it.confidence,
        })),
      });
      toast.success(`✅ Added ${pending.length} to your ${spaceLabel}`);
      setPending([]);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  // ── Delete a saved pantry item ──
  const onDeleteSaved = async (pantry_id, name) => {
    if (!window.confirm(`Remove ${name}?`)) return;
    try {
      await axios.delete(`${API}/pantry/${pantry_id}`);
      refresh();
    } catch {
      toast.error("Delete failed.");
    }
  };

  // ── Rename saved item ──
  const savedEditNameRef = useRef(null);
  const onSaveEditSaved = async (pantry_id, patch) => {
    try {
      await axios.patch(`${API}/pantry/${pantry_id}`, patch);
      setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Edit failed.");
    }
  };

  // Space-tinted card style — cool for gym, warm for kitchen
  const tintBar = space === "kitchen" ? "bg-amber-500" : "bg-slate-400";
  const tintText = space === "kitchen" ? "text-amber-300" : "text-slate-300";

  return (
    <div className="p-3 space-y-3" data-testid={`pantry-scanner-${space}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className={`text-[10px] uppercase tracking-widest font-bold ${tintText}`}>
            🥫 my {spaceLabel}
          </div>
          <div className="text-sm text-white/80">
            {space === "kitchen"
              ? "Snap the fridge / cupboard — I list what I see, you confirm."
              : "Snap your gym corner — I list what I see, you confirm."}
          </div>
        </div>
        <button
          type="button"
          onClick={onScan}
          disabled={scanning}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full ${tintBar} text-black text-[10px] font-black uppercase tracking-widest disabled:opacity-60`}
          data-testid={`pantry-scan-btn-${space}`}
        >
          {scanning ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
          {scanning ? "Scanning…" : "Scan batch"}
        </button>
      </div>

      {/* Autocomplete add */}
      <div className="rounded-lg border border-white/15 bg-black/50 p-2 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Search size={12} className="text-white/50 shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTypedFallback()}
            placeholder={space === "kitchen" ? "Type an ingredient…" : "Type equipment…"}
            className="flex-1 bg-transparent border-none outline-none text-sm text-white placeholder-white/40"
            data-testid={`pantry-search-${space}`}
          />
          {query.trim() && (
            <button
              type="button"
              onClick={addTypedFallback}
              className="p-1 rounded bg-white/10 hover:bg-white/20"
              data-testid={`pantry-add-typed-${space}`}
              title="Add as-is"
            >
              <Plus size={12} className="text-white" />
            </button>
          )}
        </div>
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {suggestions.slice(0, 12).map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => addFromCatalog(s)}
                className="text-[11px] px-2 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white/90 border border-white/10"
                data-testid={`pantry-suggest-${s.id}`}
              >
                + {s.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pending review */}
      {pending.length > 0 && (
        <div className="rounded-lg border border-emerald-400/40 bg-emerald-950/20 p-2 space-y-2" data-testid={`pantry-pending-${space}`}>
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">
              <Sparkles size={10} className="inline" /> Review · {pending.length}
            </div>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setPending([])}
                className="text-[10px] px-2 py-1 rounded bg-black/50 border border-white/20 text-white/80 hover:bg-white/10"
                data-testid="pantry-review-clear"
              >
                Discard all
              </button>
              <button
                type="button"
                onClick={onConfirmAll}
                disabled={busy}
                className="inline-flex items-center gap-1 text-[10px] px-3 py-1 rounded bg-emerald-500 text-black font-black uppercase tracking-widest hover:bg-emerald-400 disabled:opacity-60"
                data-testid="pantry-review-confirm"
              >
                {busy ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                Confirm all
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {pending.map((it) => (
              <div
                key={it._tmp}
                className="flex items-start gap-2 p-2 rounded bg-black/50 border border-white/10"
                data-testid={`pantry-pending-item-${it._tmp}`}
              >
                <div className="flex-1 min-w-0 space-y-1">
                  <input
                    type="text"
                    value={it.name}
                    onChange={(e) => patchPending(it._tmp, { name: e.target.value })}
                    className="w-full bg-transparent text-sm font-bold text-white border-b border-white/10 focus:border-emerald-400/60 outline-none"
                    data-testid={`pantry-pending-name-${it._tmp}`}
                  />
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={it.qty}
                      onChange={(e) => patchPending(it._tmp, { qty: e.target.value })}
                      placeholder={space === "kitchen" ? "qty (2 cans)" : "size (25 lb)"}
                      className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded px-1.5 py-0.5 text-[11px] text-white/90 placeholder-white/30 outline-none focus:border-white/40"
                      data-testid={`pantry-pending-qty-${it._tmp}`}
                    />
                    <span className={`text-[9px] px-1 py-0.5 rounded ${it.source === "catalog" ? "bg-emerald-500/20 text-emerald-200" : it.source === "ai" ? "bg-amber-500/20 text-amber-200" : "bg-white/10 text-white/60"}`}>
                      {it.source === "catalog" ? "known" : it.source === "ai" ? `AI ${Math.round((it.confidence || 0) * 100)}%` : "manual"}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removePending(it._tmp)}
                  className="p-1 rounded bg-black/40 hover:bg-red-500/40"
                  data-testid={`pantry-pending-remove-${it._tmp}`}
                  aria-label="Remove"
                >
                  <X size={12} className="text-red-200" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Saved list */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold mb-1.5">
          In my {spaceLabel} · {saved.length}
        </div>
        {saved.length === 0 ? (
          <div className="text-xs text-white/50 italic px-2 py-4 text-center border border-dashed border-white/20 rounded">
            Empty. Scan or type above to add.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {saved.map((it) => (
              <div
                key={it.pantry_id}
                className="relative rounded-lg border border-white/10 bg-black/50 p-2 flex flex-col gap-0.5"
                data-testid={`pantry-item-${it.pantry_id}`}
              >
                {editingId === it.pantry_id ? (
                  <>
                    <input
                      ref={savedEditNameRef}
                      type="text"
                      defaultValue={it.name}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onSaveEditSaved(it.pantry_id, { name: e.currentTarget.value });
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      className="w-full bg-black/60 border border-emerald-400/60 rounded px-1 py-0.5 text-xs font-bold text-white outline-none"
                      autoFocus
                      data-testid={`pantry-edit-name-${it.pantry_id}`}
                    />
                    <button
                      type="button"
                      onClick={() => onSaveEditSaved(it.pantry_id, { name: savedEditNameRef.current?.value })}
                      className="text-[10px] px-1 py-0.5 rounded bg-emerald-500 text-black font-bold"
                    >
                      Save
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-1">
                      <div className="text-xs font-bold text-white truncate">{it.name}</div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => setEditingId(it.pantry_id)}
                          className="p-0.5 rounded bg-black/40 hover:bg-white/10"
                          aria-label="Edit"
                          data-testid={`pantry-edit-${it.pantry_id}`}
                        >
                          <Pencil size={10} className="text-white/70" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeleteSaved(it.pantry_id, it.name)}
                          className="p-0.5 rounded bg-black/40 hover:bg-red-500/40"
                          aria-label="Remove"
                          data-testid={`pantry-delete-${it.pantry_id}`}
                        >
                          <Trash2 size={10} className="text-red-300" />
                        </button>
                      </div>
                    </div>
                    <div className="text-[10px] text-white/50 capitalize">{it.category}</div>
                    {it.qty && <div className="text-[10px] text-white/70">{it.qty}</div>}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Camera modal — opens on Scan, closes after capture */}
      <CameraCapture
        open={cameraOpen}
        title={space === "kitchen" ? "Snap your kitchen" : "Snap your gear"}
        hint={space === "kitchen" ? "Fill the frame with the fridge / cupboard / counter" : "Fill the frame with your gear"}
        onCapture={handleCapture}
        onClose={() => setCameraOpen(false)}
      />
    </div>
  );
}

export default PantryScannerPanel;
